import { createContext, useContext, useEffect, type PropsWithChildren } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchJson, type ApiError } from "@easy-core";

export type HostedSubscription = {
  provider: string;
  plan_code: string;
  status: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  entitlements: Record<string, string>;
};

export type HostedSession = {
  tenant: {
    id: number;
    public_id: string;
    slug: string;
    name: string;
  };
  user: {
    id: number;
    public_id: string;
    email: string;
    is_platform_admin: boolean;
  };
  membership: {
    role: string;
  };
  subscription: HostedSubscription | null;
};

type LoginPayload = {
  email: string;
  password: string;
};

type HostedSessionContextValue = {
  session: HostedSession | null;
  state: "loading" | "authenticated" | "anonymous" | "error";
  errorMessage: string;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  isSubmitting: boolean;
  refresh: () => Promise<HostedSession | null>;
};

const SESSION_QUERY_KEY = ["hosted-session"];

const HostedSessionContext = createContext<HostedSessionContextValue | null>(null);

async function fetchHostedSession() {
  return fetchJson<HostedSession>("/api/auth/me");
}

function isUnauthorized(error: unknown) {
  return typeof error === "object" && error !== null && (error as ApiError).status === 401;
}

function readErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

export function HostedSessionProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchHostedSession,
    retry: false,
    refetchInterval: 60000,
  });

  const loginMutation = useMutation({
    mutationFn: async (payload: LoginPayload) => {
      await fetchJson("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return fetchHostedSession();
    },
    onSuccess: (session) => {
      queryClient.removeQueries({ predicate: query => query.queryKey[0] !== "hosted-session" });
      queryClient.setQueryData(SESSION_QUERY_KEY, session);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await fetchJson("/api/auth/logout", {
        method: "POST",
      });
      return null;
    },
    onSuccess: () => {
      queryClient.removeQueries({ predicate: query => query.queryKey[0] !== "hosted-session" });
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
    },
  });

  useEffect(() => {
    if (isUnauthorized(sessionQuery.error)) queryClient.removeQueries({predicate: query => query.queryKey[0] !== "hosted-session"});
  }, [sessionQuery.error, queryClient]);

  const state: HostedSessionContextValue["state"] = sessionQuery.isLoading
    ? "loading"
    : isUnauthorized(sessionQuery.error)
    ? "anonymous"
    : sessionQuery.data
    ? "authenticated"
    : sessionQuery.error
    ? "error"
    : "anonymous";

  const errorMessage =
    state === "error" ? readErrorMessage(sessionQuery.error, "Impossible de charger la session hosted.") : "";

  return (
    <HostedSessionContext.Provider
      value={{
        session: sessionQuery.data || null,
        state,
        errorMessage,
        login: async (payload) => {
          await loginMutation.mutateAsync(payload);
        },
        logout: async () => {
          await logoutMutation.mutateAsync();
        },
        isSubmitting: loginMutation.isPending || logoutMutation.isPending,
        refresh: async () => {
          try {
            return await queryClient.fetchQuery({
              queryKey: SESSION_QUERY_KEY,
              queryFn: fetchHostedSession,
            });
          } catch (error) {
            if (isUnauthorized(error)) {
              queryClient.setQueryData(SESSION_QUERY_KEY, null);
              return null;
            }
            throw error;
          }
        },
      }}
    >
      {children}
    </HostedSessionContext.Provider>
  );
}

export function useHostedSession() {
  const context = useContext(HostedSessionContext);
  if (!context) {
    throw new Error("HostedSessionProvider is required.");
  }
  return context;
}
